'use client';

import React from 'react';
import { Modal } from '@/app/components/ui/Modal';
import { Button } from '@/app/components/ui/Button';
import { AlertTriangle, FileText, Save } from 'lucide-react';

interface ConflictModalProps {
    isOpen: boolean;
    onKeepLocal: () => void;
    onLoadRemote: () => void;
    remoteDate?: string;
}

export const ConflictModal = ({
    isOpen,
    onKeepLocal,
    onLoadRemote,
    remoteDate
}: ConflictModalProps) => {
    // Format date if provided
    const formattedDate = remoteDate
        ? new Date(remoteDate).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
        : 'a previous session';

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => { }} // Prevent closing without choice
            title="Conflicting Will Found"
            forceInteraction
        >
            <div className="space-y-6">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex gap-3 items-start">
                    <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-amber-800">
                        <p className="font-medium mb-1">We found a previously saved Will for this account.</p>
                        <p>You have started a new draft, but you also have a saved Will on file from <strong>{formattedDate}</strong>.</p>
                    </div>
                </div>

                <div className="space-y-3">
                    <p className="text-sm font-medium text-gray-700">What would you like to do?</p>

                    <button
                        onClick={onKeepLocal}
                        className="w-full flex items-center p-4 border-2 border-primary/20 hover:border-primary bg-white hover:bg-primary/5 rounded-xl transition-all group text-left"
                    >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mr-4 group-hover:bg-primary/20 transition-colors">
                            <Save className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900">Keep Current Draft</h4>
                            <p className="text-sm text-gray-500">Save what is currently on your screen (Overwrites old Will)</p>
                        </div>
                    </button>

                    <button
                        onClick={onLoadRemote}
                        className="w-full flex items-center p-4 border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 rounded-xl transition-all group text-left"
                    >
                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mr-4 group-hover:bg-gray-200 transition-colors">
                            <FileText className="w-5 h-5 text-gray-600" />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900">Load Saved Will</h4>
                            <p className="text-sm text-gray-500">Discard changes and load your previously saved Will</p>
                        </div>
                    </button>
                </div>
            </div>
        </Modal>
    );
};
